import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { SessionAuthService } from '../services/session-auth.service';
import { ConfigService } from '../../core/services/config.service';
import { OAuthUrlService } from '../services/oauth-url.service';

@Injectable({
  providedIn: 'root'
})
export class SessionAuthGuard implements CanActivate {

  constructor(
    private sessionAuthService: SessionAuthService,
    private router: Router,
    private configService: ConfigService,
    private oauthUrlService: OAuthUrlService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.sessionAuthService.status$.pipe(
      switchMap((status) => {
        switch (status) {
          case 'loading':
            return of(false);

          case 'unauthenticated':
            this.redirectToPortalAliados();
            return of(false);

          case 'authenticated':
            return this.validateUserAccess();

          default:
            this.redirectToPortalAliados();
            return of(false);
        }
      }),
      catchError((error) => {
        console.error('Error in auth guard:', error);
        this.redirectToPortalAliados();
        return of(false);
      })
    );
  }

  private validateUserAccess(): Observable<boolean> {
    return this.sessionAuthService.validateSession().pipe(
      map((isValid) => {
        if (!isValid) {
          this.redirectToPortalAliados();
          return false;
        }

        const user = this.sessionAuthService.getUser();

        if (!user) {
          this.redirectToPortalAliados();
          return false;
        }

        if (!user.rol?.name) {
          console.error('Access denied: User must have a configured role');
          return false;
        }

        if (!user.enabled) {
          console.error('Access denied: User is disabled');
          return false;
        }

        return true;
      }),
      catchError((error) => {
        console.error('Error validating user access:', error);
        this.redirectToPortalAliados();
        return of(false);
      })
    );
  }

  private redirectToPortalAliados(errorMessage?: string): void {
    this.performRedirect(errorMessage).catch((error) => {
      console.error('Error in redirection:', error);
      const config = this.configService.getConfig();
      window.location.href = config.portalAliadosUrl;
    });
  }

  private async performRedirect(errorMessage?: string): Promise<void> {
    try {
      const oauthUrl = await this.oauthUrlService.buildPortalAliadosOAuthUrl();

      if (errorMessage) {
        const url = new URL(oauthUrl);
        url.searchParams.set('error', errorMessage);
        window.location.href = url.toString();
      } else {
        window.location.href = oauthUrl;
      }
    } catch (error) {
      const config = this.configService.getConfig();
      window.location.href = config.portalAliadosUrl;
    }
  }
}
